import type { Entry } from "@/shared/cache";
import type {
	ApiErrorEnvelope,
	ApiResponse,
	RequestOptions,
} from "@/shared/types/request";

/**
 * Dependencies the action factories close over.
 */
export type ActionDeps = {
	makeApiRequest: (opts: RequestOptions) => Promise<ApiResponse>;
	updateEntries: (updater: (prev: Entry[]) => Entry[]) => () => void;
	refreshEntries: () => Promise<void>;
};

export function plural(n: number) {
	return n === 1 ? "entry" : "entries";
}

export function actingHeaders(timekeeperId?: string | null) {
	return timekeeperId
		? { "x-acting-for": timekeeperId, "x-act-as-prof-id": timekeeperId }
		: undefined;
}

/**
 * Structured error thrown by `apiCall` when a v2 response carries a full error
 * envelope (`code`, `type`, `conflicts`, etc.). Older paths still throw plain
 * `Error` so callers that don't need to branch on `code` can keep working.
 */
export class ApiError extends Error {
	readonly envelope: ApiErrorEnvelope;
	constructor(envelope: ApiErrorEnvelope) {
		super(envelope.message);
		this.name = "ApiError";
		this.envelope = envelope;
	}
	get code() {
		return this.envelope.code;
	}
	get apiType() {
		return this.envelope.type;
	}
	get param() {
		return this.envelope.param;
	}
	get conflicts() {
		return this.envelope.conflicts;
	}
}

export async function apiCall(
	deps: ActionDeps,
	opts: RequestOptions,
): Promise<void> {
	const resp = await deps.makeApiRequest(opts);
	if (resp.status === "ERROR") {
		if (resp.apiError) throw new ApiError(resp.apiError);
		throw new Error(resp.error || "Request failed");
	}
}

/**
 * Refresh the canonical entries cache after a mutation settles.
 */
export function refreshEntries(deps: ActionDeps): Promise<void> {
	return deps.refreshEntries();
}
